import { useEffect, useState } from "react";
import { useGetList } from "react-admin";
import { administrationRequests } from "api/administrationApi";
import { studentRequests } from "api/studentApi";
import { StudyTypes } from "utils/constants";
import { ALL_OPTIONS } from "./constants";

/**
 * Draft + options of the semester pre-filter (foreign languages / sport / streams schedules, make-ups, scholarship
 * campaigns), for a list to pass straight to <CustomFilter>:
 *
 *   const semester = useSemesterFilter({ filters, withLanguage: true, withStudyType: true });
 *   <CustomFilter filters={semester.fields} buttonDisabled={!semester.isComplete}
 *     buttonOnClick={() => setFilters(semester.query)} />
 *
 * `filters` is the list's committed useStore object - it seeds the draft. Cascade: year (-> instruction language)
 * (-> study type) -> semester. `sessionField` - the query key of the semester (the APIs differ). The first year and
 * the full-time study type are preselected; a language or semester with a single option is picked automatically.
 * `onChange` is called when the user changes any of these fields, so the caller can clear its own dependent fields.
 * Returns `fields`, `isComplete` and `query` (the draft without empty values).
 */
const useSemesterFilter = ({
  filters,
  withLanguage = false,
  withStudyType = false,
  sessionField = "academicSessionType",
  onChange,
}) => {
  const [academicYearId, setAcademicYearId] = useState(filters?.academicYearId ?? null);
  const [instructionLanguageId, setInstructionLanguageId] = useState(filters?.instructionLanguageId ?? null);
  const [studyTypeId, setStudyTypeId] = useState(filters?.studyTypeId ?? StudyTypes.Full_time);
  const [session, setSession] = useState(filters?.[sessionField] ?? null);

  const hasLanguage = Boolean(academicYearId && (!withLanguage || instructionLanguageId));
  const hasSemesterInputs = Boolean(hasLanguage && (!withStudyType || studyTypeId));

  const { data: academicYears } = useGetList(studentRequests.getAcademicYearsSelect(), ALL_OPTIONS);
  const { data: languages } = useGetList(administrationRequests.getInstructionLanguagesSelect(), ALL_OPTIONS, {
    enabled: Boolean(withLanguage && academicYearId),
  });
  const { data: studyTypes } = useGetList(administrationRequests.getStudyTypesSelect(), ALL_OPTIONS, {
    enabled: Boolean(withStudyType && hasLanguage),
  });
  const { data: semesters } = useGetList(
    studentRequests.getAcademicSessionTypesSelect(),
    {
      ...ALL_OPTIONS,
      filter: {
        academicYearId,
        semestersOnly: true,
        ...(withLanguage && { instructionLanguageId }),
        ...(withStudyType && { studyTypeId }),
      },
    },
    { enabled: hasSemesterInputs },
  );

  useEffect(() => {
    if (!academicYearId && academicYears?.length) setAcademicYearId(academicYears[0].id);
  }, [academicYearId, academicYears]);
  useEffect(() => {
    if (withLanguage && !instructionLanguageId && languages?.length === 1) setInstructionLanguageId(languages[0].id);
  }, [withLanguage, instructionLanguageId, languages]);
  useEffect(() => {
    if (!session && semesters?.length === 1) setSession(semesters[0].id);
  }, [session, semesters]);

  // Each level clears the ones below it; the study type falls back to full-time.
  const changeYear = value => {
    onChange?.();
    setAcademicYearId(value);
    setInstructionLanguageId(null);
    setStudyTypeId(StudyTypes.Full_time);
    setSession(null);
  };
  const changeLanguage = value => {
    onChange?.();
    setInstructionLanguageId(value);
    setStudyTypeId(StudyTypes.Full_time);
    setSession(null);
  };
  const changeStudyType = value => {
    onChange?.();
    setStudyTypeId(value);
    setSession(null);
  };

  const changeSession = value => {
    onChange?.();
    setSession(value);
  };

  const select = (label, value, setValue, choices) => ({
    label,
    type: "select",
    value: value ?? "",
    setValue,
    choices: choices ?? [],
  });

  const fields = [
    select("Академична година", academicYearId, changeYear, academicYears),
    ...(withLanguage && academicYearId
      ? [select("Език на обучение", instructionLanguageId, changeLanguage, languages)]
      : []),
    ...(withStudyType && hasLanguage ? [select("Вид на обучение", studyTypeId, changeStudyType, studyTypes)] : []),
    ...(hasSemesterInputs ? [select("Семестър", session, changeSession, semesters)] : []),
  ];

  const isComplete = Boolean(hasSemesterInputs && session);

  const draft = {
    academicYearId,
    ...(withLanguage && { instructionLanguageId }),
    ...(withStudyType && { studyTypeId }),
    [sessionField]: session,
  };
  const query = Object.fromEntries(Object.entries(draft).filter(([, value]) => value != null && value !== ""));

  return { fields, isComplete, query };
};

export default useSemesterFilter;
